import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    Avatar,
    Card,
    CardContent,
    useTheme,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    ArrowBack as ArrowBackIcon,
    LocationOn as LocationIcon,
    Description as DescriptionIcon,
    Delete as DeleteIcon,
    AccountCircle as AccountCircleIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Home as HomeIcon,
    DirectionsCar as CarIcon,
    School as SchoolIcon,
    Money as MoneyIcon,
    AccountBalance as AccountBalanceIcon,
    MonetizationOn as MonetizationOnIcon,
    VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { fetchBorrowerByUuid, deleteBorrower, getUserByUuid, deleteUser, getLenderById, deleteLender, getAllRoles, getConsultantById, deleteConsultantById } from '../../services/userService';
import { toast } from 'react-toastify';

const UserDetails = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Determine user type from the URL path
    const userType = useMemo(() => (
        location.pathname.includes('/users/borrowers/') 
        ? 'borrower' 
        : location.pathname.includes('/users/lenders/')
            ? 'lender'
            : location.pathname.includes('/users/consultants/')
                ? 'consultant'
                : 'manager'
    ), [location.pathname]);

    const determineManagerRole = async (managerData) => {
        try {
            const localStorageRoleId = parseInt(localStorage.getItem('roleId'), 10);
            
            if (localStorageRoleId === 1) {
                return 'SUPERADMIN';
            }

            const rolesData = await getAllRoles();
            if (!Array.isArray(rolesData)) return 'User';

            const matchingRole = rolesData.find(r => r.roleId === managerData.roleId);
            return matchingRole?.role || 'User';
        } catch (error) {
            toast.error(error.message);
            return 'User';
        }
    };

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                setError(null);
                
                let userData;
                switch(userType) {
                    case 'borrower': {
                        userData = await fetchBorrowerByUuid(uuid);
                        if (!userData.result) throw new Error('Borrower not found');
                        const borrowerData = Array.isArray(userData.result)
                            ? userData.result.find(b => b.uuid === uuid)
                            : userData.result;

                        if (!borrowerData) throw new Error('Borrower not found');

                                    setUser({
                                        ...borrowerData,
                                        entityName: borrowerData.entityName || '',
                                        formattedBorrowerType: getBorrowerTypeLabel(borrowerData.borrowerType),
                            lender_financialInstitutionType: borrowerData.lender_financialInstitutionType
                        });
                        break;
                    }
                    
                    case 'lender': {
                        userData = await getLenderById(uuid);
                        if (!userData.result?.lender) throw new Error('Lender not found');

                            const lenderData = userData.result.lender;
                            setUser({
                                id: lenderData.id,
                                uuid: lenderData.lender_lenderId,
                                name: lenderData.name,
                                email: lenderData.email || 'N/A',
                                mobileNumber: lenderData.mobileNumber || 'N/A',
                                location: lenderData.lender_location,
                                financialInstitutionType: lenderData.lender_financialInstitutionType,
                                financialInstitution: lenderData.lender_financialInstitution,
                                typeOfLending: lenderData.lender_typeOfLending,
                                typeOfRetailLoans: lenderData.lender_typeOfRetailLoans,
                                designation: lenderData.lender_designation,
                                status: lenderData.status,
                                verified: lenderData.lender_verified,
                                role: 'Lender',  // Set default role for lenders
                                createDatetime: lenderData.createDatetime,
                                commitDatetime: lenderData.commitDatetime,
                                createOpName: lenderData.createOpName,
                                commitOpName: lenderData.commitOpName,
                                ip: lenderData.ip,
                                latitude: lenderData.latitude,
                                longitude: lenderData.longitude
                            });
                        break;
                    }
                        
                    case 'consultant': {
                        const res = await getConsultantById(uuid);
                        const consultantData = res?.result?.result || res?.result || res;
                        if (!consultantData?.id) throw new Error('Consultant not found');
                        setUser({
                            id: consultantData.id,
                            uuid: consultantData.uuid,
                            name: consultantData.name,
                            email: consultantData.email,
                            mobileNumber: consultantData.mobileNumber,
                            status: consultantData.status,
                            companyName: consultantData.companyName,
                            professionalWebsite: consultantData.professionalWebsite,
                            yearsOfExperience: consultantData.yearsOfExperience,
                            loanSpecialization: consultantData.loanSpecialization,
                            createDatetime: consultantData.createDatetime,
                            commitDatetime: consultantData.commitDatetime,
                            createOpName: consultantData.createOpName,
                            commitOpName: consultantData.commitOpName,
                            ip: consultantData.ip,
                            latitude: consultantData.latitude,
                            longitude: consultantData.longitude,
                            role: 'Consultant'
                        });
                        break;
                    }
                    default: { // manager
                        userData = await getUserByUuid(uuid);
                        if (!userData?.uuid && !userData?.result?.uuid) {
                            throw new Error('User not found');
                        }

                        const managerData = userData.result || userData;
                        const role = await determineManagerRole(managerData);
                        
                        setUser({
                            ...managerData,
                            role
                        });
                    }
                }
            } catch (err) {
                console.error('Error loading user details:', err);
                setError(err.message || 'Failed to load user details');
            } finally {
                setLoading(false);
            }
        };

        if (uuid) {
            loadUser();
        }
    }, [uuid, userType]);

    const getBorrowerTypeLabel = (borrowerType) => {
        if (!borrowerType) return 'N/A';
        
        const type = borrowerType.toLowerCase();
        if (type === 'individual') return 'Individual';
        if (type === 'company') return 'Company';
        if (type === 'partnership_firm' || type === 'partnership') return 'Partnership';
        if (type === 'propreitorship_firm' || type === 'proprietorship') return 'Proprietorship';
        
        return borrowerType;
    };
    
    const getLendingTypeLabel = (lendingType) => {
        if (!lendingType) return 'N/A';
        
        const type = lendingType.toUpperCase();
        switch (type) {
            case 'PERSONAL': return 'Personal Loan';
            case 'BUSINESS': return 'Business Loan';
            case 'HOME': return 'Home Loan';
            case 'VEHICLE': return 'Vehicle Loan';
            case 'EDUCATION': return 'Education Loan';
            default: return lendingType;
        }
    };
    
    const getFinancierTypeLabel = (financierType) => {
        if (!financierType) return 'N/A';
        
        const type = financierType.toUpperCase();
        switch (type) {
            case 'BANK': return 'Bank';
            case 'NBFC': return 'NBFC';
            case 'CREDIT_UNION': return 'Credit Union';
            case 'PRIVATE': return 'Private Lender';
            case 'GOVERNMENT': return 'Government';
            default: return financierType;
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };
    
    const getLendingTypeIcon = (lendingType) => {
        if (!lendingType) return <MoneyIcon />;
        
        const type = lendingType.toUpperCase();
        switch (type) {
            case 'PERSONAL': return <PersonIcon />;
            case 'BUSINESS': return <BusinessIcon />;
            case 'HOME': return <HomeIcon />;
            case 'VEHICLE': return <CarIcon />;
            case 'EDUCATION': return <SchoolIcon />;
            default: return <MoneyIcon />;
        }
    };

    const getFinancialInstitutionTypeLabel = (type) => {
        if (!type) return 'N/A';
        
        const institutionType = type.toLowerCase();
        switch (institutionType) {
            case 'bank': return 'Bank';
            case 'nbfc': return 'NBFC';
            case 'credit_union': return 'Credit Union';
            case 'private': return 'Private Lender';
            case 'government': return 'Government';
            default: return type;
        }
    };

    const getTypeOfLendingLabel = (type) => {
        if (!type) return 'N/A';
        
        const lendingType = type.toLowerCase();
        switch (lendingType) {
            case 'secured': return 'Secured';
            case 'unsecured': return 'Unsecured';
            case 'both': return 'Both';
            default: return type;
        }
    };

    const getTypeOfRetailLoansLabel = (type) => {
        if (!type) return 'N/A';
        
        const retailType = type.toLowerCase();
        switch (retailType) {
            case 'personal': return 'Personal Loan';
            case 'business': return 'Business Loan';
            case 'home': return 'Home Loan';
            case 'vehicle': return 'Vehicle Loan';
            case 'education': return 'Education Loan';
            default: return type;
        }
    };

    const handleOpenDeleteDialog = () => {
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const handleDeleteUser = async () => {
        if (deleteLoading) return;

        try {
            setDeleteLoading(true);
            
            switch(userType) {
                case 'borrower':
                    await deleteBorrower(uuid);
                    break;
                    
                case 'lender':
                    await deleteLender(user.id);
                    break;
                
                case 'consultant':
                    await deleteConsultantById(user.id);
                    break;
                    
                default: // manager
                    await deleteUser(uuid);
            }
            
            setSnackbar({
                open: true,
                message: `${userType === 'borrower' ? 'Borrower' : userType === 'lender' ? 'Lender' : 'User'} deleted successfully!`,
                severity: 'success'
            });
            
            setDeleteDialogOpen(false);
            
            setTimeout(() => {
                navigate(`/users/${userType}s`);
            }, 1500);
        } catch (error) {
            console.error(`Error deleting ${userType}:`, error);
            setSnackbar({
                open: true,
                message: error.message || `Failed to delete ${userType}`,
                severity: 'error'
            });
            setDeleteDialogOpen(false);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button 
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/users/${userType}s`)}
                    sx={{ mt: 2 }}
                >
                    Back to {userType === 'borrower' ? 'Borrowers' : userType === 'lender' ? 'Lenders' : 'Users'}
                </Button>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">No user information found</Alert>
                <Button 
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/users/${userType}s`)}
                    sx={{ mt: 2 }}
                >
                    Back to {userType === 'borrower' ? 'Borrowers' : userType === 'lender' ? 'Lenders' : 'Users'}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button 
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/users/${userType}s`)}
                        sx={{ mr: 2 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        {userType === 'borrower' ? 'Borrower' : userType === 'lender' ? 'Lender' : 'User'} Details
                    </Typography>
                </Box>
                <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenDeleteDialog}
                >
                    Delete {userType === 'borrower' ? 'Borrower' : userType === 'lender' ? 'Lender' : 'User'}
                </Button>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            borderRadius: 3, 
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.1)}`
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                                {user.profilePhotoUrl ? (
                                    <Avatar 
                                        src={user.profilePhotoUrl}
                                        alt={user.name || 'User'}
                                        sx={{ 
                                            width: 100, 
                                            height: 100, 
                                            mb: 2,
                                            border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                        }}
                                    />
                                ) : (
                                    <Avatar 
                                        sx={{ 
                                            width: 100, 
                                            height: 100, 
                                            mb: 2, 
                                            bgcolor: theme.palette.primary.main,
                                            fontSize: '2rem'
                                        }}
                                    >
                                        {getInitials(user.name)}
                                    </Avatar>
                                )}
                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {user.name || 'N/A'}
                                </Typography>
                                {userType === 'borrower' ? (
                                    <Chip 
                                        label={user.formattedBorrowerType}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                    />
                                ) : userType === 'lender' ? (
                                    <Chip 
                                        label={getFinancialInstitutionTypeLabel(user.financialInstitutionType)}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                    />
                                ) : (
                                    <Chip 
                                        label={user.role || 'User'}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                    />
                                )}
                                
                                {userType === 'manager' && user.username && (
                                    <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                                        @{user.username}
                                    </Typography>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                    Contact Information
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmailIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                    <Typography variant="body2">
                                        {user.email || 'N/A'}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PhoneIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                    <Typography variant="body2">
                                        {user.mobileNumber || user.phoneNumber || 'N/A'}
                                    </Typography>
                                </Box>
                                
                                {userType === 'borrower' && user.address && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                        <LocationIcon sx={{ color: theme.palette.text.secondary, mr: 1.5, mt: 0.3 }} />
                                        <Typography variant="body2">
                                            {user.address}
                                        </Typography>
                                    </Box>
                                )}
                                
                                {userType === 'manager' && (
                                    <>
                                        {user.region && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                <LocationIcon sx={{ color: theme.palette.text.secondary, mr: 1.5, mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Region
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {user.region}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        {user.area && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                <LocationIcon sx={{ color: theme.palette.text.secondary, mr: 1.5, mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Area
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {user.area}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                            
                            {userType === 'manager' && (user.lendingType || user.financierType) && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            Lending Information
                                        </Typography>
                                        
                                        {user.lendingType && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                {getLendingTypeIcon(user.lendingType)}
                                                <Box sx={{ ml: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Lending Type
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {getLendingTypeLabel(user.lendingType)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        
                                        {user.financierType && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <BusinessIcon sx={{ color: theme.palette.text.secondary }} />
                                                <Box sx={{ ml: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Financier Type
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {getFinancierTypeLabel(user.financierType)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}

                            {userType === 'lender' && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            Institution Details
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <AccountBalanceIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Institution Type
                                                </Typography>
                                                <Typography variant="body2">
                                                    {getFinancialInstitutionTypeLabel(user.financialInstitutionType)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <BusinessIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Financial Institution
                                                </Typography>
                                                <Typography variant="body2">
                                                    {user.financialInstitution || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            Lending Information
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <MonetizationOnIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Type of Lending
                                                </Typography>
                                                <Typography variant="body2">
                                                    {getTypeOfLendingLabel(user.typeOfLending)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <DescriptionIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Type of Retail Loans
                                                </Typography>
                                                <Typography variant="body2">
                                                    {getTypeOfRetailLoansLabel(user.typeOfRetailLoans)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            Professional Details
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <PersonIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Designation
                                                </Typography>
                                                <Typography variant="body2">
                                                    {user.designation || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        {user.organization && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <BusinessIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Organization
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {user.organization}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <VerifiedUserIcon sx={{ color: theme.palette.text.secondary, mr: 1.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Verification Status
                                                </Typography>
                                                <Typography variant="body2">
                                                    <Chip 
                                                        size="small"
                                                        label={user.verified ? "Verified" : "Pending"}
                                                        color={user.verified ? "success" : "warning"}
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            borderRadius: 3, 
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.1)}`,
                            height: '100%'
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                {userType === 'borrower' ? 'Borrower' : userType === 'lender' ? 'Lender' : 'User'} Information
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        User ID
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {user.id || 'N/A'}
                                    </Typography>
                                </Grid>

                                {userType === 'borrower' ? (
                                    // Borrower-specific fields
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Borrower Type
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.formattedBorrowerType || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        {user.entityName && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Entity Name
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.entityName}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.gstNumber && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    GST Number
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.gstNumber}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </>
                                ) : userType === 'lender' ? (
                                    // Lender-specific fields
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                                                Basic Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                User ID
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.id || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Lender ID
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.uuid || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Name
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.name || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Email
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.email || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Mobile Number
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.mobileNumber || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Status
                                            </Typography>
                                            <Chip
                                                label={user.status || 'N/A'}
                                                color={user.status === 'active' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Verification Status
                                            </Typography>
                                            <Chip
                                                label={user.verified ? 'Verified' : 'Pending'}
                                                color={user.verified ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" sx={{ mb: 2, mt: 3 }}>
                                                Institution Details
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Institution Type
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.financialInstitutionType || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Financial Institution
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.financialInstitution || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Type of Lending
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.typeOfLending || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Type of Retail Loans
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.typeOfRetailLoans || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Designation
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.designation || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Location
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.location || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" sx={{ mb: 2, mt: 3 }}>
                                                System Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Created By
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.createOpName || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Last Updated By
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.commitOpName || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Created Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.createDatetime ? new Date(user.createDatetime).toLocaleString() : 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Last Updated Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.commitDatetime ? new Date(user.commitDatetime).toLocaleString() : 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                IP Address
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.ip || 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Location Coordinates
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.latitude && user.longitude ? `${user.latitude}, ${user.longitude}` : 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                ) : (
                                    // Manager-specific fields
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Username
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.username || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Role
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {user.role || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        
                                        {user.roleId && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Role ID
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.roleId}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.uuid && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    UUID
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.uuid}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.userId && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    User ID
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.userId}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.status && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Status
                                                </Typography>
                                                <Chip
                                                    label={user.status}
                                                    color={user.status.toLowerCase() === 'active' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </Grid>
                                        )}

                                        {user.lendingType && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Lending Type
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {getLendingTypeLabel(user.lendingType)}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.financierType && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Financier Type
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {getFinancierTypeLabel(user.financierType)}
                                                </Typography>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                                                System Information
                                            </Typography>
                                        </Grid>

                                        {user.createOpName && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Created By
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.createOpName}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.commitOpName && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Last Updated By
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.commitOpName}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.createDatetime && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Created Date
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {new Date(user.createDatetime).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.commitDatetime && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Last Updated Date
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {new Date(user.commitDatetime).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {user.ip && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    IP Address
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.ip}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {(user.latitude || user.longitude) && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Location Coordinates
                                                </Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {user.latitude && user.longitude ? `${user.latitude}, ${user.longitude}` : 'N/A'}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                        Location Information
                                    </Typography>
                                </Grid>

                                {user.latitude && user.longitude && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Coordinates
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {`${user.latitude}, ${user.longitude}`}
                                        </Typography>
                                    </Grid>
                                )}

                                {user.ip && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            IP Address
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {user.ip}
                                        </Typography>
                                    </Grid>
                                )}

                                {userType === 'borrower' && user.screen && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Device Screen
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {user.screen}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {user.createDatetime && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Created On
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {new Date(user.createDatetime).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {user.commitDatetime && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Last Updated
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {new Date(user.commitDatetime).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                )}

                                {userType === 'lender' && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <LocationIcon sx={{ color: theme.palette.text.secondary, mr: 1.5, mt: 0.3 }} />
                                            <Typography variant="body2">
                                                {user.location || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: theme.palette.error.main }}>
                    Delete {userType === 'borrower' ? 'Borrower' : userType === 'lender' ? 'Lender' : 'User'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the {userType === 'borrower' ? 'borrower' : userType === 'lender' ? 'lender' : 'user'} "{user?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteUser} 
                        color="error" 
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserDetails; 